import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Form, Input, InputNumber, Select, Button, Alert, Card, Upload, Popconfirm, message, Image } from 'antd';
import { resolveImageSrc, handleImageError, getImageForCard } from '../../../utils/images';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import ModernImageUpload from '../../UI/ModernImageUpload';

function VendorProfile() {
	const [antForm] = Form.useForm();
	const [cities, setCities] = useState([]);
	const [vendor, setVendor] = useState(null);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');
	const [uploading, setUploading] = useState(false);
	const [logoImage, setLogoImage] = useState(null);
	const [bannerImage, setBannerImage] = useState(null);

	useEffect(() => {
		const load = async () => {
			try {
				const [citiesRes, meRes] = await Promise.all([
					api.getCities(),
					api.getMyVendor().catch(() => null),
				]);
				setCities(citiesRes.data);
				if (meRes && meRes.data) {
					setVendor(meRes.data);
					antForm.setFieldsValue({
						business_name: meRes.data.business_name || '',
						description: meRes.data.description || '',
						phone: meRes.data.phone || '',
						email: meRes.data.email || '',
						address: meRes.data.address || '',
						city_id: String(meRes.data.city_id || ''),
						pincode: meRes.data.pincode || '',
						min_order_amount: meRes.data.min_order_amount || 0,
						preparation_time: meRes.data.preparation_time || 60,
					});
					
					// Set logo and banner images if they exist
					if (meRes.data.logo_image_path) {
						setLogoImage(resolveImageSrc(meRes.data.logo_image_path));
					}
					
					if (meRes.data.cover_image_path) {
						setBannerImage(resolveImageSrc(meRes.data.cover_image_path));
					}
				}
			} catch (e) {
				// ignore
			}
		};
		load();
	}, [antForm]);

	const onFinish = async (values) => {
		setSaving(true);
		setMessage('');
		setError('');
		try {
			const res = await api.saveMyVendor(values);
			setVendor(res.data);
			setMessage('Vendor profile saved');
		} catch (err) {
			setError(err.message || 'Failed to save');
		} finally {
			setSaving(false);
		}
	};

	const onUpload = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setUploading(true);
		setError('');
		setMessage('');
		try {
			await api.uploadMyVendorImage(file, { type: 'gallery' });
			const meRes = await api.getMyVendor();
			setVendor(meRes.data);
			setMessage('Image uploaded');
		} catch (err) {
			setError(err.message || 'Upload failed');
		} finally {
			setUploading(false);
		}
	};

	const onDeleteImage = async (imageId) => {
		if (!vendor) return;
		try {
			await api.request(`/vendors/${vendor.id}/images/${imageId}`, { method: 'DELETE' });
			const meRes = await api.getMyVendor();
			setVendor(meRes.data);
			message.success('Image deleted');
		} catch (err) {
			message.error(err.message || 'Delete failed');
		}
	};

	const handleLogoUpload = async (file) => {
		try {
			const response = await api.uploadVendorMedia(file, 'vendor-logo');
			await api.saveMyVendor({
				logo_image_path: response.data.file_path
			});
			setLogoImage(response.data.file_path);
			message.success('Logo updated successfully');
		} catch (err) {
			message.error(err.message || 'Failed to update logo');
		}
	};

	const handleBannerUpload = async (file) => {
		try {
			const response = await api.uploadVendorMedia(file, 'vendor-banner');
			await api.saveMyVendor({
				cover_image_path: response.data.file_path
			});
			setBannerImage(response.data.file_path);
			message.success('Banner updated successfully');
		} catch (err) {
			message.error(err.message || 'Failed to update banner');
		}
	};

	return (
		<div className="max-w-3xl mx-auto p-6">
			<h1 className="text-2xl font-bold mb-6">Vendor Profile</h1>
			{message && <Alert type="success" message={message} className="mb-4" />}
			{error && <Alert type="error" message={error} className="mb-4" />}

			<Card className="mb-8" title="Business Details">
				<Form layout="vertical" form={antForm} onFinish={onFinish} initialValues={{
					business_name: '', description: '', phone: '', email: '', address: '', city_id: '', pincode: '', min_order_amount: 0, preparation_time: 60,
				}}>
					<Form.Item label="Business Name" name="business_name" rules={[{ required: true, message: 'Please enter business name' }]}>
						<Input />
					</Form.Item>
					<Form.Item label="Description" name="description">
						<Input.TextArea rows={3} />
					</Form.Item>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<Form.Item label="Phone" name="phone" rules={[{ required: true, message: 'Please enter phone' }]}>
							<Input />
						</Form.Item>
						<Form.Item label="Email" name="email" rules={[{ required: true, type: 'email', message: 'Please enter email' }]}>
							<Input />
						</Form.Item>
					</div>
					<Form.Item label="Address" name="address" rules={[{ required: true, message: 'Please enter address' }]}>
						<Input />
					</Form.Item>
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
						<Form.Item label="City" name="city_id" rules={[{ required: true, message: 'Please select city' }]}>
							<Select options={cities.map(c => ({ label: c.name, value: String(c.id) }))} />
						</Form.Item>
						<Form.Item label="Pincode" name="pincode" rules={[{ required: true, message: 'Please enter pincode' }]}>
							<Input />
						</Form.Item>
						<Form.Item label="Min Order Amount" name="min_order_amount">
							<InputNumber min={0} style={{ width: '100%' }} />
						</Form.Item>
					</div>
					<Form.Item>
						<Button type="primary" htmlType="submit" loading={saving}>Save Profile</Button>
					</Form.Item>
				</Form>
			</Card>

			{/* Logo Upload */}
			<Card className="mb-8" title="Business Logo">
				<div className="space-y-4">
					<p className="text-gray-600">Upload your business logo. This will be displayed on your vendor profile and in search results.</p>
					<ModernImageUpload
						currentImage={logoImage}
						onUpload={handleLogoUpload}
						onRemove={() => {
							setLogoImage(null);
							api.saveMyVendor({ logo_image_path: null });
						}}
					/>
				</div>
			</Card>

			{/* Banner Upload */}
			<Card className="mb-8" title="Cover Banner">
				<div className="space-y-4">
					<p className="text-gray-600">Upload a cover banner for your business. This will be displayed as the main image on your vendor profile.</p>
					<ModernImageUpload
						currentImage={bannerImage}
						onUpload={handleBannerUpload}
						onRemove={() => {
							setBannerImage(null);
							api.saveMyVendor({ cover_image_path: null });
						}}
					/>
				</div>
			</Card>

			<div className="mt-10">
				<h2 className="text-xl font-semibold mb-3">Gallery</h2>
				<label className="inline-flex items-center gap-2">
					<input type="file" onChange={onUpload} disabled={uploading} hidden />
					<Button icon={<UploadOutlined />} disabled={uploading}>Upload Image</Button>
				</label>
				<div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
					{vendor?.images?.map((img) => (
						<div key={img.id} className="rounded overflow-hidden border relative group">
							{/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
							<img src={getImageForCard(img)} alt={img.alt_text || 'Vendor image'} className="w-full h-32 object-cover" onError={handleImageError} />
							<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition">
								<Popconfirm title="Delete image?" onConfirm={() => onDeleteImage(img.id)}>
									<Button size="small" danger icon={<DeleteOutlined />} />
								</Popconfirm>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

export default VendorProfile;
